import {
  AlertTriangle,
  Bell,
  CalendarDays,
  ClipboardCheck,
  FileText,
  Users,
  Monitor,
  QrCode,
  Smartphone,
  UsersRound,
} from 'lucide-react'
import { AppPaths } from '@/config/app-paths'
import type { StaffNavContext } from '../staff-nav-context'
import { group, leaf, type StaffNavGroup, type StaffNavNode } from '../nav-node'

export function buildTeamGroup(ctx: StaffNavContext): StaffNavGroup | null {
  const children: StaffNavNode[] = []

  if (ctx.showCoordinatorAttendance) {
    children.push(
      leaf(
        'coordinator_attendance',
        'My attendance',
        AppPaths.payrollCoordinatorAttendance,
        ClipboardCheck,
        ['attendance', 'coordinator', 'mark'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showCoordinatorReview) {
    children.push(
      leaf(
        'coordinator_review',
        'Coordinators',
        AppPaths.payrollCoordinatorTeam,
        UsersRound,
        ['coordinator', 'approve', 'attendance', 'team'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showTeamAttendance) {
    children.push(
      leaf(
        'team_attendance',
        'Attendance',
        AppPaths.payrollTeamAttendance,
        ClipboardCheck,
        [
          'attendance',
          'roster',
          'supervisor',
          'mark',
          'location',
          'site',
          'team attendance',
        ],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showAvailablePool) {
    children.push(
      leaf(
        'available_pool',
        'Pool',
        AppPaths.payrollAvailablePool,
        Users,
        ['pool', 'available', 'released', 'offboard', 'unassigned', 'claim'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showAttendanceIssues) {
    children.push(
      leaf(
        'attendance_issues',
        'Attendance Issues',
        AppPaths.payrollAttendanceIssues,
        AlertTriangle,
        [
          'attendance',
          'issues',
          'face',
          'late',
          'missing',
          'offline',
          'supervisor',
          'dispute',
          'hr',
          'mobile',
          'app',
        ],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showSupervisorBulkHiring) {
    children.push(
      leaf(
        'supervisor_bulk_hiring',
        'Bulk hiring',
        AppPaths.supervisorBulkHiring,
        QrCode,
        ['bulk', 'hiring', 'qr', 'joining form', 'supervisor'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showHiringRequisitions) {
    children.push(
      leaf(
        'supervisor_hiring_requisitions',
        'Hiring requisitions',
        AppPaths.hiringRequisitions,
        FileText,
        ['hiring', 'requisition', 'visa', 'offer', 'supervisor'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showSupervisorAppSignIns) {
    children.push(
      leaf(
        'supervisor_app_sign_ins',
        'Mobile app',
        AppPaths.supervisorMobileApp,
        Smartphone,
        ['mobile', 'app', 'registered', 'installed', 'sign in', 'login', 'phone'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showAttendanceNotifications) {
    children.push(
      leaf(
        'attendance_notifications',
        'Notifications',
        AppPaths.payrollAttendanceNotifications,
        Bell,
        [
          'attendance',
          'notification',
          'push',
          'alert',
          'supervisor',
          'reminder',
          'late',
          'sync',
          'Ghurair',
          'site',
          'team notifications',
        ],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showSupervisorStaffDocs) {
    children.push(
      leaf(
        'supervisor_staff_docs',
        'Staff Documents',
        AppPaths.supervisorStaffDocs,
        FileText,
        ['documents', 'passport', 'eid', 'visa', 'supervisor'],
        { activeWhenPrefix: true },
      ),
    )
  }

  // Self-attendance review is temporarily hidden from the sidebar (route still exists).

  if (ctx.showLeavesNav) {
    children.push(
      leaf(
        'leave_management',
        'Leave Management',
        AppPaths.adminLeaves,
        CalendarDays,
        ['leave', 'annual', 'sick', 'time off', 'absence', 'leave management'],
        { activeWhenPrefix: true },
      ),
    )
  }

  // Supervisor desk hides Account; surface enrolled "My activity" under Team instead.
  if (ctx.showMyDesktopActivityNav && !ctx.showAccountSection) {
    children.push(
      leaf('my_desktop_activity', 'My activity', AppPaths.myDesktopActivity, Monitor, [
        'desktop',
        'activity',
        'timer',
        'added time',
      ]),
    )
  }

  return group('team', 'Team', UsersRound, ['team', 'attendance', 'supervisor', 'field'], children)
}
